import './Products.css';
import React, { useState } from 'react';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { Pagination } from '../../components/Pagination/Pagination';
import { SortableList } from '../../components/SortableList/SortableList';
import { usePaginatedResponse, useData, useTask } from '../../model/data';
import { min, sum, flatten, max, compact } from 'lodash';
import { getProducts, ProductPatch, Product, MerchProductVariant } from '../../model/product';
import { serializeQuery } from '../../model/filter';
import { useMeta } from '../../model/meta';
import { AddProduct } from './AddProduct';
import { productPath } from '../../routes';
import { StatusPill } from '../../components/StatusPill/StatusPill';
import { formatCurrency, coalesce } from '../../util/util';
import { getVariantActiveState } from './Product/ProductVariants';
import { useQueryAsState, SortDirection } from '../../model/history';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { t } from '../../locales/translate';
import { ControlledModal } from '../../components/Modal/ControlledModal';
import { runProductListExport } from '../../model/exports';
import { SortByIcon } from '../../components/SortableList/SortByIcon';
import { PaginatedPermissionButton } from '../../components/Button/PaginatedPermissionButton';
import { Icon } from '../../components/Icon/Icon';
import { Stack } from '../../components/Stack/Stack';
import { FormMultiSelect } from '../../components/Form/Select/FormMultiSelect';

export function Products(): JSX.Element {
  // HOOKS
  useMeta({ breadcrumbs: [{ title: t.model.product.merchandise }, { title: t.model.product._plural }] });
  const [params] = useQueryAsState();

  function filterAndSort(): {
    term?: string;
    status?: string[];
    sortBy?: string;
    sortDir?: SortDirection;
    lowAvailability?: boolean;
  } {
    return {
      term: serializeQuery(params.query),
      status: params.status,
      sortBy: params.sortBy || 'created_at',
      sortDir: params.sortDir || 'desc',
      lowAvailability: params.availability === 'low',
    };
  }

  const paginator = usePaginatedResponse(
    (page, perPage) => getProducts({ ...filterAndSort(), page, perPage }),
    JSON.stringify(params)
  );
  const products = useData((db) => compact(paginator.currentIds.map((e) => db.table('products').find(e))));

  const [addModal, setAddModal] = useState(false);
  const downloadCsvTask = useTask({ handleError: true });

  function getProductVariantsProp<T extends Extract<keyof MerchProductVariant, string>>(
    productVariants: MerchProductVariant[],
    prop: T
  ): Array<MerchProductVariant[T]> {
    return productVariants.map((item) => item[prop]);
  }

  function getMinPrice(product: Product): string {
    if (product.productVariants) {
      const filteredPrices = flatten(getProductVariantsProp(product.productVariants, 'prices'))
        .filter((item) => item.active)
        .map((item) => item.amount);

      if (filteredPrices.length === 0) {
        return '-';
      }

      return `${filteredPrices.length > 0 ? 'from ' : ''}${formatCurrency(min(filteredPrices))}`;
    }

    return '-';
  }

  function getStock(product: Product, stockType: Extract<keyof MerchProductVariant, string>): number {
    if (product.productVariants) {
      return sum(getProductVariantsProp(product.productVariants, stockType));
    }
    return 0;
  }

  function isLowAvailability(minAvailability = 0, available = 0): boolean {
    return available < minAvailability;
  }

  function hasLowAvailabilityVariant(productVariants?: MerchProductVariant[]): boolean {
    if (productVariants) {
      const availableStock = getProductVariantsProp(productVariants, 'availableCount');
      const minimumStock = getProductVariantsProp(productVariants, 'minimumStockCount');
      return availableStock.some((stock, index) => isLowAvailability(minimumStock?.[index], stock));
    }
    return false;
  }

  // TEMPLATE
  return (
    <Container className="products">
      <Card
        className="products__list"
        title={t.model.product._plural}
        pagination={<Pagination {...paginator} />}
        elevation={2}
        fullbleed
        actions={
          <div className="products__actions">
            <PaginatedPermissionButton
              onClick={() => setAddModal(true)}
              name="plus"
              model={paginator}
              permission="create"
            />
            <PaginatedPermissionButton
              onClick={() => downloadCsvTask.run(runProductListExport(filterAndSort()))}
              name="download"
              tooltip={t.actions.downloadAsCSV}
              model={paginator}
              permission="export_product_list"
              hideLock
            />
          </div>
        }
      >
        <SortableList
          error={paginator.error}
          items={products}
          rowLink={(row) => productPath(row)}
          actions={
            <FilterBar
              filters={['query', 'status']}
              filterData={{
                statusData: ['draft', 'in_review', 'on_sale', 'stopped'],
              }}
              extraFilters={[
                {
                  key: 'availability',
                  renderer: (value, onChange) => {
                    return (
                      <FormMultiSelect
                        className="products__availability-filter"
                        value={value?.split(',').filter(Boolean)}
                        onChange={(v) => {
                          onChange(v?.filter(Boolean).join(','));
                        }}
                        label={t.model.product.availability}
                        options={[{ id: 'low', label: t.model.product.low }]}
                      />
                    );
                  },
                },
              ]}
            />
          }
          loading={paginator.loading}
          columns={[
            {
              label: t.model.product.sku,
              dataKey: 'sku',
            },
            {
              label: t.model.product.artistBrand,
              apiSortIcon: <SortByIcon sortBy="artist" />,
              dataKey: 'artist',
            },
            {
              label: t.general.type,
              apiSortIcon: <SortByIcon sortBy="type" />,
              dataKey: 'productType',
            },
            {
              label: t.general.motive,
              apiSortIcon: <SortByIcon sortBy="name" />,
              dataKey: 'name',
            },
            {
              label: t.general.color,
              apiSortIcon: <SortByIcon sortBy="color" />,
              dataKey: 'color',
            },
            {
              label: t.model.price._name,
              dataKey: 'price',
              cellRenderer: (row) => {
                return <div className="products__price">{getMinPrice(row)}</div>;
              },
            },
            {
              label: t.model.product.stock,
              dataKey: 'stock',
              cellRenderer: (row) => {
                return <div className="products__stock">{getStock(row, 'stockCount')}</div>;
              },
            },
            {
              label: t.model.status.available,
              dataKey: 'available',
              cellRenderer: (row) => {
                return (
                  <Stack line size="xxs">
                    {getStock(row, 'availableCount')}{' '}
                    {hasLowAvailabilityVariant(row.productVariants) && (
                      <Icon name="problem--sm" className="products__stock-icon" />
                    )}
                  </Stack>
                );
              },
            },
            {
              label: t.model.status.reserved,
              dataKey: 'reserved',
              cellRenderer: (row) => {
                return <div className="products__stock">{getStock(row, 'reservedCount')}</div>;
              },
            },
            {
              label: t.model.status.cart,
              dataKey: 'cart',
              cellRenderer: (row) => {
                return <div className="products__stock">{getStock(row, 'cartCount')}</div>;
              },
            },
            {
              label: t.model.product.fulfillment,
              dataKey: 'fulfillment',
              cellRenderer: (row) => {
                return (
                  <div className="products__stock">
                    {getStock(row, 'pendingDeliveryCount')}&nbsp;/&nbsp;
                    {getStock(row, 'fulfillmentCount')}
                  </div>
                );
              },
            },
            {
              label: t.model.status.incomplete,
              dataKey: 'incomplete',
              cellRenderer: (row) => {
                return <div className="products__stock">{getStock(row, 'incompleteCount')}</div>;
              },
            },
            {
              label: t.model.status._name,
              dataKey: 'status',
              cellRenderer: (row) => <StatusPill status={row.status} />,
            },
          ]}
          expandable={(row) => !!row.productVariants && row.productVariants.length > 0}
          expandedContent={(row) => {
            const lastOrder = (max(row.productVariants?.map((v) => v.order)) || 0) + 1;
            return (
              row.productVariants && (
                <SortableList
                  size="xs"
                  items={row.productVariants.sort(
                    (a, b) => coalesce(a.order, lastOrder) - coalesce(b.order, lastOrder)
                  )}
                  columns={[
                    {
                      label: t.model.productVariant.sku,
                      dataKey: 'sku',
                      cellRenderer: (subRow) => subRow.sku || '-',
                    },
                    {
                      label: t.model.variant._name,
                      dataKey: 'description',
                      cellRenderer: (subRow) => subRow.description || '-',
                    },
                    {
                      label: t.model.product.minAvailability,
                      dataKey: 'minimumStockCount',
                      cellRenderer: (subRow) => subRow.minimumStockCount || 0,
                    },
                    {
                      label: t.model.product.stock,
                      dataKey: 'stockCount',
                    },
                    {
                      label: t.model.status.available,
                      dataKey: 'availableCount',
                      cellRenderer: (subRow) => (
                        <Stack line size="xxs">
                          {subRow.availableCount}{' '}
                          {isLowAvailability(subRow.minimumStockCount, subRow.availableCount) && (
                            <Icon name="problem--sm" className="products__stock-icon" />
                          )}
                        </Stack>
                      ),
                    },
                    {
                      label: t.model.status.reserved,
                      dataKey: 'reservedCount',
                    },
                    {
                      label: t.model.status.cart,
                      dataKey: 'cartCount',
                    },
                    {
                      label: t.model.status.fulfillment,
                      dataKey: 'fulfillmentCount',
                      // eslint-disable-next-line no-irregular-whitespace
                      cellRenderer: (subRow) => `${subRow.pendingDeliveryCount} / ${subRow.fulfillmentCount}`,
                    },
                    {
                      label: t.model.status.incomplete,
                      dataKey: 'incompleteCount',
                    },
                    {
                      label: t.model.status._name,
                      dataKey: 'activeFrom',
                      cellRenderer: (subRow) => getVariantActiveState(subRow),
                    },
                  ]}
                />
              )
            );
          }}
        />
      </Card>
      <ControlledModal<Partial<ProductPatch>>
        open={addModal}
        model={{} as Partial<ProductPatch>}
        onClose={() => setAddModal(false)}
        title={t.actions.add(t.model.product._name)}
        size="small"
      >
        {(controller) => <AddProduct controller={controller} />}
      </ControlledModal>
    </Container>
  );
}
